import React, { useState, useEffect, useRef, useCallback } from 'react';
import { WifiOff, RefreshCw, CheckCircle2 } from 'lucide-react';
import { pingServer } from '../services/api';
import ClinicLogo from './ClinicLogo';

const RETRY_SECONDS = 6;
// A stopped server refuses a check within milliseconds. The button shows "Checking connection..."
// for at least this long, so a press is seen to do something.
const MIN_CHECK_MS = 700;
const wait = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

/**
 * Full-page state shown when the application server is unreachable (api.js dispatches
 * 'clinic:server-unreachable' after confirming with a /health ping). Keeps retrying on its
 * own; the moment the server answers again the app reloads so every module refetches fresh
 * data instead of sitting on broken zeros.
 */
const ServerDownScreen = () => {
  const [checking, setChecking] = useState(false);
  const [reconnected, setReconnected] = useState(false);
  const [countdown, setCountdown] = useState(RETRY_SECONDS);
  // Whether the last check found no answer, so the screen can say so.
  const [noAnswer, setNoAnswer] = useState(false);
  const aliveRef = useRef(true);
  // Set on every mount, not only when the ref is created. The development build mounts each
  // component twice and runs this cleanup in between, so a flag set only by useRef(true) stayed
  // false: every check's result was dropped and the screen hung on "Checking connection...".
  useEffect(() => {
    aliveRef.current = true;
    return () => { aliveRef.current = false; };
  }, []);

  const tryReconnect = useCallback(async () => {
    setChecking(true);
    const [ok] = await Promise.all([pingServer(), wait(MIN_CHECK_MS)]);
    if (!aliveRef.current) return;
    if (ok) {
      setReconnected(true);
      setTimeout(() => window.location.reload(), 1200);
    } else {
      setNoAnswer(true);
      setCountdown(RETRY_SECONDS);
      setChecking(false);
    }
  }, []);

  // Countdown between automatic retries; pauses while a check is in flight.
  useEffect(() => {
    if (checking || reconnected) return undefined;
    if (countdown <= 0) {
      tryReconnect();
      return undefined;
    }
    const t = setTimeout(() => setCountdown((c) => c - 1), 1000);
    return () => clearTimeout(t);
  }, [countdown, checking, reconnected, tryReconnect]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-navy-800 via-navy-900 to-navy-950 p-6">
      <div className="max-w-md w-full text-center">
        <ClinicLogo size={64} variant="white" className="mx-auto" />

        <div className="mt-8 mx-auto w-16 h-16 rounded-2xl bg-white/10 border border-white/20 flex items-center justify-center">
          {reconnected ? (
            <CheckCircle2 className="w-8 h-8 text-emerald-200" />
          ) : (
            <WifiOff className="w-8 h-8 text-emerald-200" />
          )}
        </div>

        {reconnected ? (
          <>
            <h1 className="mt-6 text-2xl font-bold text-white">Connection restored</h1>
            <p className="mt-2 text-emerald-100 text-sm">Taking you back to the app...</p>
          </>
        ) : (
          <>
            <h1 className="mt-6 text-2xl font-bold text-white">We can't reach the server</h1>
            <p className="mt-3 text-emerald-100 text-sm leading-relaxed">
              The application server is not responding right now. This is usually temporary.
              Nothing you saved has been lost, and we'll keep trying to reconnect automatically.
            </p>
            {/* Centred like the English above it. Inline, because App.css right-aligns every
                .urdu-text; Nastaliq needs the tall line or its two lines paint over each other. */}
            <p
              className="mt-2 text-emerald-200/90 urdu-text text-base"
              dir="rtl"
              style={{ textAlign: 'center', lineHeight: 2.5 }}
            >
              سرور سے رابطہ نہیں ہو پا رہا۔ براہ کرم چند لمحے انتظار کریں، ہم دوبارہ کوشش کر رہے ہیں۔
            </p>

            <div className="mt-8 flex flex-col items-center gap-3">
              <button
                onClick={tryReconnect}
                disabled={checking}
                className="inline-flex items-center gap-2 px-5 py-2.5 bg-white text-emerald-700 rounded-lg font-semibold text-sm hover:bg-emerald-50 disabled:opacity-60"
              >
                <RefreshCw className={`w-4 h-4 ${checking ? 'animate-spin' : ''}`} />
                <span>{checking ? 'Checking connection...' : 'Try Again Now'}</span>
              </button>
              {/* Always present, so a screen reader hears the result of each check. */}
              <p role="status" className={noAnswer && !checking ? 'text-sm font-medium text-white' : 'sr-only'}>
                {noAnswer && !checking ? 'Still no answer from the server.' : ''}
              </p>
              {!checking && (
                <p className="text-xs text-emerald-200">
                  Retrying automatically in {countdown}s
                </p>
              )}
            </div>

            <p className="mt-10 text-xs text-emerald-300/70">
              If this keeps happening, contact your administrator.
            </p>
          </>
        )}
      </div>
    </div>
  );
};

export default ServerDownScreen;
